import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  Check,
  Clipboard,
  Crown,
  DoorClosed,
  Link2,
  Play,
  Radio,
  Share2,
  UsersRound,
  UserRoundPlus,
} from "lucide-react";
import type { CSSProperties } from "react";
import type { ConnectionState } from "../../hooks/useSkyjoGame";
import type { SocialClient } from "../../hooks/useSocial";
import type { PlayerAccount, RoomPlayer, RoomState } from "../../types/protocol";
import { AvatarPhoto } from "../ui/Avatar";
import { Brand } from "../ui/Brand";
import { DangerAction } from "../ui/DangerAction";

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

/** Teinte du compte quand il y en a un, sinon la couleur dérivée du siège. */
const avatarStyle = (account: PlayerAccount, seat: number): CSSProperties =>
  account
    ? ({ "--avatar-gradient": `var(--avatar-${account.avatarId})` } as CSSProperties)
    : ({ "--avatar-hue": `${(seat * 57 + 242) % 360}` } as CSSProperties);

interface PlayerSeatProps {
  player?: RoomPlayer;
  seat: number;
  isSelf: boolean;
  /** Rendu seulement pour un joueur inscrit qui n'est pas encore un ami. */
  onAddFriend?: () => void;
  addFriendBusy?: boolean;
  addFriendDone?: boolean;
}

const PlayerSeat = ({
  player,
  seat,
  isSelf,
  onAddFriend,
  addFriendBusy = false,
  addFriendDone = false,
}: PlayerSeatProps) => (
  <motion.li
    className={`player-seat${player ? " is-filled" : ""}${isSelf ? " is-self" : ""}`}
    layout
    initial={{ opacity: 0, scale: 0.92 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: seat * 0.025 }}
  >
    {player ? (
      <>
        <span
          className={`player-seat__avatar${player.account ? " player-seat__avatar--account" : ""}`}
          style={avatarStyle(player.account, player.seat)}
        >
          {initials(player.account?.displayName ?? player.name)}
          {/* Avant la pastille de présence, qui doit rester lisible par-dessus. */}
          <AvatarPhoto profile={player.account} />
          <span className={`presence-dot${player.connected ? " is-online" : ""}`} />
        </span>
        <span className="player-seat__identity">
          <strong>{player.name}{isSelf ? " (toi)" : ""}</strong>
          <small>
            {player.isHost && <><Crown size={12} /> Hôte</>}
            {!player.isHost && (player.connected ? "Dans la partie" : "Reconnexion…")}
            {player.account && <span className="seat-handle">#{player.account.discriminator}</span>}
          </small>
        </span>
        {addFriendDone ? (
          <span className="seat-friend-done" title="Demande envoyée"><Check size={14} /></span>
        ) : onAddFriend ? (
          <button
            className="icon-button seat-friend-add"
            type="button"
            onClick={onAddFriend}
            disabled={addFriendBusy}
            aria-label={`Ajouter ${player.account?.handle ?? player.name} en ami`}
            title="Ajouter en ami"
          >
            {addFriendBusy ? (
              <span className="button-spinner" aria-hidden="true" />
            ) : (
              <UserRoundPlus size={16} />
            )}
          </button>
        ) : null}
        {player.isHost && <span className="host-badge" title="Hôte"><Crown size={14} /></span>}
      </>
    ) : (
      <>
        <span className="player-seat__avatar player-seat__avatar--empty"><UserRoundPlus size={19} /></span>
        <span className="player-seat__identity"><strong>Place libre</strong><small>En attente d’un joueur</small></span>
      </>
    )}
  </motion.li>
);

interface LobbyScreenProps {
  room: RoomState;
  connection: ConnectionState;
  pendingAction: string | null;
  social: SocialClient;
  onStart: () => Promise<boolean>;
  onLeave: () => void;
  onCloseTable: () => Promise<boolean>;
  onOpenFriends: () => void;
}

export const LobbyScreen = ({
  room,
  connection,
  pendingAction,
  social,
  onStart,
  onLeave,
  onCloseTable,
  onOpenFriends,
}: LobbyScreenProps) => {
  const [copied, setCopied] = useState(false);
  const [askedFriends, setAskedFriends] = useState<string[]>([]);
  const self = room.players.find((player) => player.id === room.selfPlayerId);
  const isHost = Boolean(self?.isHost);
  const minimum = room.minPlayers ?? 2;
  const maximum = room.maxPlayers ?? 8;
  const canStart = Boolean(
    self?.isHost &&
    room.players.length >= minimum &&
    room.players.every((player) => player.connected) &&
    connection === "connected",
  );
  const busy = pendingAction === "start-game";
  const closing = pendingAction === "close-room";
  const hasDisconnectedPlayer = room.players.some((player) => !player.connected);
  const signedIn = social.snapshot !== null;
  const selfUserId = social.snapshot?.self.userId ?? null;
  const knownUserIds = new Set([
    ...social.friends.map((friend) => friend.userId),
    ...[...social.incoming, ...social.outgoing].map((request) => request.profile.userId),
  ]);

  const addFriend = async (userId: string, handle: string) => {
    const ok = await social.requestFriend(handle);
    if (ok) setAskedFriends((current) => [...current, userId]);
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(room.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_800);
    } catch {
      setCopied(false);
    }
  };

  const share = async () => {
    if (!navigator.share) {
      await copyCode();
      return;
    }
    try {
      await navigator.share({
        title: "Rejoins ma table Skyjo",
        text: `Rejoins ma partie de Skyjo avec le code ${room.code}`,
        url: `${window.location.origin}?room=${room.code}`,
      });
    } catch {
      // Une annulation du partage ne demande aucun retour d'erreur.
    }
  };

  return (
    <main className="lobby-shell">
      <header className="app-header">
        {/* Le libellé disparaît sous 540 px : sans `aria-label` le bouton
            n'aurait plus de nom du tout pour un lecteur d'écran. */}
        <button
          className="ghost-button ghost-button--compact"
          type="button"
          onClick={onLeave}
          aria-label="Quitter le salon"
        >
          <ArrowLeft size={17} /> <span>Quitter</span>
        </button>
        <div className="app-header__brand"><Brand compact /></div>
      </header>

      <motion.section
        className={`lobby-content ${isHost ? "is-host" : "is-guest"}`}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="lobby-intro">
          <span className="eyebrow"><Radio size={14} /> Salon en direct</span>
          <h1>{isHost ? "Invite les joueurs" : "Tu es dans la partie"}</h1>
          <p>
            {isHost
              ? `Partage ce code. La partie pourra commencer dès que vous serez au moins ${minimum}.`
              : "L’hôte lancera la partie dès que tout le monde sera prêt."}
          </p>
        </div>

        <div className={`room-code-card glass-panel${isHost ? "" : " is-guest"}`}>
          <div className="room-code-card__pattern" aria-hidden="true">
            {Array.from({ length: 12 }, (_, index) => <i key={index} />)}
          </div>
          <span className="room-code-card__label">Code de la table</span>
          <button className="room-code" type="button" onClick={copyCode} aria-label={`Copier le code ${room.code}`}>
            {room.code.split("").map((character, index) => (
              <motion.span
                key={`${character}-${index}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.045 }}
              >
                {character}
              </motion.span>
            ))}
          </button>
          <div className="room-code-card__actions">
            <button className="secondary-button" type="button" onClick={copyCode}>
              {copied ? <Check size={17} /> : <Clipboard size={17} />}
              {copied ? "Copié !" : "Copier le code"}
            </button>
            <button className="icon-button icon-button--surface" type="button" onClick={share} aria-label="Partager l’invitation">
              <Share2 size={18} />
            </button>
            {signedIn && (
              <button
                className="secondary-button room-code-card__invite"
                type="button"
                onClick={onOpenFriends}
                aria-label="Ouvrir la liste d’amis pour inviter"
              >
                <UsersRound size={17} /> Inviter des amis
              </button>
            )}
          </div>
          <AnimatePresence>
            {copied && (
              <motion.span
                className="copy-confirmation"
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                role="status"
              >
                <Link2 size={13} /> Code prêt à être partagé
              </motion.span>
            )}
          </AnimatePresence>
        </div>

        <section className="players-panel glass-panel" aria-labelledby="players-title">
          <div className="players-panel__head">
            <div>
              <span className="section-kicker">Table</span>
              <h2 id="players-title">Joueurs connectés</h2>
            </div>
            <span className="player-count"><strong>{room.players.length}</strong> / {maximum}</span>
          </div>
          <ul className="player-seats">
            {Array.from({ length: maximum }, (_, seat) => {
              const player = room.players.find((candidate) => candidate.seat === seat) ?? room.players[seat];
              const account = player?.account ?? null;
              const asked = account !== null && askedFriends.includes(account.userId);
              const addable =
                signedIn &&
                account !== null &&
                account.userId !== selfUserId &&
                !knownUserIds.has(account.userId);
              return (
                <PlayerSeat
                  key={player?.id ?? `empty-${seat}`}
                  player={player}
                  seat={seat}
                  isSelf={player?.id === room.selfPlayerId}
                  onAddFriend={
                    addable && account && !asked
                      ? () => void addFriend(account.userId, account.handle)
                      : undefined
                  }
                  addFriendBusy={social.pending === "request"}
                  addFriendDone={asked}
                />
              );
            })}
          </ul>
        </section>

        <footer className="lobby-actions">
          {/* Sans la pastille de connexion, le bouton « Lancer » se griserait
              sans explication : le salon dit lui-même ce qui bloque. */}
          {connection !== "connected" ? (
            <div className="waiting-host" role="status">
              <span className="waiting-pulse"><i /><i /></span>
              <div>
                <strong>{connection === "offline" ? "Serveur injoignable" : "Reconnexion en cours…"}</strong>
                <small>Ta place reste réservée dans le salon.</small>
              </div>
            </div>
          ) : self?.isHost ? (
            <>
              <p>
                <UsersRound size={16} />
                {room.players.length < minimum
                  ? `Encore ${minimum - room.players.length} joueur${minimum - room.players.length > 1 ? "s" : ""} pour commencer`
                  : hasDisconnectedPlayer
                    ? "On attend la reconnexion de tout le monde"
                    : "Tout est prêt. Lance quand tu veux !"}
              </p>
              <button className="primary-button primary-button--launch" type="button" disabled={!canStart || busy} onClick={() => void onStart()}>
                {busy ? <span className="button-spinner" /> : <Play size={19} fill="currentColor" />}
                {busy ? "Lancement…" : "Lancer la partie"}
              </button>
              {/* Quitter en tant qu'hôte passe la main ; fermer dissout la
                  table. Les deux sorties n'ont pas le même sens, elles ne
                  partagent donc pas le même bouton. */}
              <DangerAction
                label="Fermer la table"
                question="Fermer pour tout le monde ?"
                confirmLabel="Fermer"
                icon={<DoorClosed size={16} />}
                pending={closing}
                disabled={busy}
                onConfirm={() => void onCloseTable()}
              />
            </>
          ) : (
            <div className="waiting-host" role="status">
              <span className="waiting-pulse"><i /><i /></span>
              <div><strong>En attente de l’hôte</strong><small>La partie commencera très bientôt.</small></div>
            </div>
          )}
        </footer>
      </motion.section>
    </main>
  );
};
